import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import AddTransactionModal from "./AddTransactionModal";
import TransactionFilter from "./TransactionFilterModal";
import BillingLogModal from "./BillingLogModal";
import SupplierDetailsCard from "./SupplierDetailsCard";
import SupplierInfoCard from "./SupplierInfoCard";
import SupplierTransactionTable from "./SupplierTransactionTable";
import { getSupplierById, exportAggregatorTransactions, getAggregatorTransactions, filterAggregatorTransactions } from "../../../services/supplierService";
import { errorToast } from "../../Toasts/Toasts";
import { getMasterData } from "../../../services/userService";

const SupplierDetails = () => {
  const { supplierId } = useParams();
  const [showAddTransaction, setShowAddTransaction] = useState(false);
  const [showTransactionFilter, setShowTransactionFilter] = useState(false);
  const [showBillingLog, setShowBillingLog] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const [flag, setFlag] = useState(false);
  const [loading, setLoading] = useState(true);
  const [masterData, setMasterData] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [transactionsLoading, setTransactionsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  const fetchSupplier = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const data = await getSupplierById(supplierId);
      setSupplierData(data);
    } catch (error) {
      console.error('Error fetching supplier:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    if (supplierId) fetchSupplier();
  }, [supplierId, flag, fetchSupplier]);

  useEffect(() => {
    fetchMasterData();
    // eslint-disable-next-line
  }, []);

  const fetchMasterData = async () => {
    try {
      const response = await getMasterData();
      setMasterData(response?.data);
    } catch (error) {
      console.error(error?.message);
    }
  };


  useEffect(() => {
    if (supplierId) {
      setTransactions([]);
      setPage(1);
      setHasMore(true);
      fetchTransactions(1);
    }
  }, [supplierId]);

  const fetchTransactions = async (pageNum = 1, pageSize = 10) => {
    try {
      setTransactionsLoading(true);
      const data = await getAggregatorTransactions(supplierId, pageNum, pageSize);
      const result = Array.isArray(data) ? data
        : Array.isArray(data?.data) ? data.data
        : Array.isArray(data?.AggregatorTransactions) ? data.AggregatorTransactions
        : [];
      
      if (pageNum === 1) {
        setTransactions(result);
      } else {
        setTransactions(prev => [...prev, ...result]);
      }
      
      // Check if there are more transactions to load
      setHasMore(result.length === pageSize);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      errorToast('Error fetching transactions');
    } finally {
      setTransactionsLoading(false);
    }
  };

  const loadMoreData = useCallback(() => {
    if (!transactionsLoading && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchTransactions(nextPage);
    }
  }, [transactionsLoading, hasMore, page, supplierId]);

  const refreshTransactions = async () => {
    try {
      setTransactions([]);
      setPage(1);
      setHasMore(true);
      await Promise.all([fetchTransactions(1), fetchSupplier(true)]);
    } catch (error) {
      console.error('Error refreshing transactions:', error);
    }
  };

  const handleChipRemove = async (key) => {
    const updated = { ...activeFilters };

    if (key === "startDate") {
      delete updated.startDate;
      delete updated.endDate;
    } else {
      delete updated[key];
    }

    setActiveFilters(updated);

    try {
      let data;
      if (Object.keys(updated).length === 0) {
        data = await getAggregatorTransactions(supplierId);
      } else {
        data = await filterAggregatorTransactions(supplierId, updated);
      }
      const result = Array.isArray(data) ? data
        : Array.isArray(data?.data) ? data.data
        : Array.isArray(data?.AggregatorTransactions) ? data.AggregatorTransactions
        : [];
      setTransactions(result);
    } catch (error) {
      errorToast("Error updating transactions");
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportAggregatorTransactions(supplierId, activeFilters);
      const blob = new Blob([response], { type: "application/vnd.ms-excel; charset=utf-8" });
      const url = URL.createObjectURL(blob);
      let alink = document.createElement("a");
      alink.href = url;
      alink.download = "Supplier_Transactions_Report.xlsx";
      alink.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = error?.response?.data?.message?.trim() || "Error downloading file";
      errorToast(errorMessage);
    }
  };

  return (
    <div className="content-wrapper">
      <div className="container-fluid flex-grow-1 p-2">
        <div className="d-flex justify-content-between align-items-center mb-0">
          <h4 className="pt-1 mb-1">Supplier Details</h4>
        </div>

        {loading ? (
          <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Supplier Info and Bank Details */}
            <SupplierInfoCard supplier={supplierData} masterData={masterData} flag={flag} setFlag={setFlag} />

            {/* Payments, Consumed, Balance Cards */}
            <SupplierDetailsCard supplier={supplierData} />

            {/* Transactions Table */}
            <SupplierTransactionTable
              transactions={transactions}
              transactionsLoading={transactionsLoading}
              setShowAddTransaction={setShowAddTransaction}
              setShowTransactionFilter={setShowTransactionFilter}
              setShowBillingLog={setShowBillingLog}
              setSelectedTransaction={setSelectedTransaction}
              onExport={handleExport}
              infiniteScrollProps={{
                dataLength: transactions.length,
                next: loadMoreData,
                hasMore: hasMore,
                loader: transactionsLoading ? <h4>Loading more transactions...</h4> : null,
                scrollableTarget: "supplierTransactionTable",
                endMessage: (
                  <p style={{ textAlign: 'center' }}>
                    <b>No more transactions to load</b>
                  </p>
                ),
                height: 400
              }}
              activeFilters={activeFilters}
              onChipRemove={handleChipRemove}
              onSuccess={refreshTransactions}
            />
          </>
        )}

        {/* Modals */}
        {showAddTransaction && <AddTransactionModal showAddTransaction={showAddTransaction} setShowAddTransaction={setShowAddTransaction} supplierId={supplierId} onSuccess={refreshTransactions} supplierData={supplierData} />}
        {showTransactionFilter && <TransactionFilter showTransactionFilter={showTransactionFilter} setShowTransactionFilter={setShowTransactionFilter} supplierId={supplierId} setTransactions={setTransactions} setActiveFilters={setActiveFilters} activeFilters={activeFilters} />}
        {showBillingLog && <BillingLogModal showBillingLog={showBillingLog} setShowBillingLog={setShowBillingLog} transactionData={selectedTransaction} onSuccess={refreshTransactions} />}
      </div>
    </div>
  );
};

export default SupplierDetails;
